import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listOrgArtists,
  createOrgArtist,
  updateOrgArtist,
  removeOrgArtist,
  inviteArtist,
  type ArtistRosterResponse,
} from "@/api/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Music, UserPlus, Trash2, Mail, Shield, Info, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ARTIST_ROLE_LABELS: Record<string, string> = {
  viewer: "Viewer",
  editor: "Editor",
  admin: "Admin",
};

interface ArtistRosterProps {
  orgId: string;
  canManage: boolean;
  isPersonal?: boolean;
}

function statusBadge(artist: ArtistRosterResponse) {
  const { invite_status, profile } = artist;

  if (invite_status === "active" && profile.user_id) {
    return (
      <span className="rounded-full text-xs font-medium px-2.5 py-1 border border-ok/40 bg-ok/20 text-ok">
        Linked
      </span>
    );
  }

  if (invite_status === "pending") {
    return (
      <span className="rounded-full text-xs font-medium px-2.5 py-1 border border-warn/40 bg-warn/20 text-warn">
        Pending
      </span>
    );
  }

  return (
    <span className="rounded-full text-xs font-medium px-2.5 py-1 border border-line-2 bg-panel-3 text-ink-3">
      Not invited
    </span>
  );
}

export function ArtistRoster({ orgId, canManage, isPersonal }: ArtistRosterProps) {
  const queryClient = useQueryClient();

  const { data: artists, isLoading } = useQuery({
    queryKey: ["org-artists", orgId],
    queryFn: () => listOrgArtists(orgId),
    enabled: !!orgId,
  });

  // --- Dialog state ---
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState("viewer");
  const [sendInvite, setSendInvite] = useState(false);

  // --- Inline invite state ---
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");

  // --- Mutations ---
  const createMutation = useMutation({
    mutationFn: (data: { display_name: string; email?: string; role?: string; send_invite?: boolean }) =>
      createOrgArtist(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-artists", orgId] });
      setShowAddDialog(false);
      setAddName("");
      setAddEmail("");
      setAddRole("viewer");
      setSendInvite(false);
      toast.success("Artist added");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to add artist");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ profileId, role }: { profileId: string; role: string }) =>
      updateOrgArtist(orgId, profileId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-artists", orgId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update role");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (profileId: string) => removeOrgArtist(orgId, profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-artists", orgId] });
      toast.success("Artist removed");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to remove artist");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: ({ profileId, email }: { profileId: string; email: string }) =>
      inviteArtist(orgId, profileId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-artists", orgId] });
      setInvitingId(null);
      setInviteEmail("");
      toast.success("Invite sent");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to send invite");
    },
  });

  // --- Handlers ---
  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data: { display_name: string; email?: string; role?: string; send_invite?: boolean } = {
      display_name: addName.trim(),
      role: addRole,
    };
    if (addEmail.trim()) {
      data.email = addEmail.trim();
      if (sendInvite) {
        data.send_invite = true;
      }
    }
    createMutation.mutate(data);
  }

  if (isPersonal) {
    return (
      <div className="card p-8 text-center">
        <Info className="mx-auto h-8 w-8 text-ink-3" />
        <p className="mt-3 text-sm font-medium text-ink-2">
          Artist roster is available in organizations
        </p>
        <p className="mt-1.5 text-sm text-ink-3">
          To manage an artist roster and send invites, create a dedicated organization
          for your festival, agency, or collective.
        </p>
        <Link
          to="/dashboard"
          className="btn btn--ghost mt-4 gap-2"
        >
          <Building2 className="h-4 w-4" />
          Create Organization
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-ink-3">Loading artists...</p>;
  }

  const artistList = artists ?? [];

  // Shared height class for all row controls (select, buttons)
  const controlCls = "h-8 rounded-md border border-line-3 text-sm";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <Music className="h-5 w-5 text-ink-3" />
          Artists
          <span className="text-sm text-ink-3">({artistList.length})</span>
        </h3>
        {canManage && (
          <button
            onClick={() => setShowAddDialog(true)}
            className="btn btn--ghost gap-1.5 px-3 py-1.5"
          >
            <UserPlus className="h-4 w-4" />
            Add Artist
          </button>
        )}
      </div>

      {/* Empty state */}
      {artistList.length === 0 && (
        <div className="card mt-6 p-8 text-center">
          <Music className="mx-auto h-8 w-8 text-ink-4" />
          <p className="mt-3 text-sm text-ink-3">No artists in this organization yet.</p>
          {canManage && (
            <button
              onClick={() => setShowAddDialog(true)}
              className="btn btn--ghost mt-3 px-3 py-1.5"
            >
              Add your first artist
            </button>
          )}
        </div>
      )}

      {/* Artist list */}
      {artistList.length > 0 && (
        <div className="mt-4 space-y-1">
          {artistList.map((artist) => (
            <div
              key={artist.profile.id}
              className="flex flex-wrap items-center gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-panel-2 sm:px-4"
            >
              {/* Avatar + name — always together, pushes the rest to wrap on
                  narrow viewports. */}
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-panel-3 text-xs font-medium text-ink-2">
                  {artist.profile.display_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {artist.profile.display_name}
                  </p>
                  {artist.festival_names.length > 0 ? (
                    <p className="text-xs text-ink-3 truncate">
                      {artist.festival_names.length} festival{artist.festival_names.length !== 1 ? "s" : ""}
                      {" — "}
                      {artist.festival_names.join(", ")}
                    </p>
                  ) : (
                    <p className="text-xs text-ink-3">No festivals</p>
                  )}
                </div>
              </div>

              {/* Status + role + actions, wraps below avatar+name on mobile. */}
              <div className="ml-11 flex flex-wrap items-center gap-2 sm:ml-0">
                {/* Status badge */}
                {statusBadge(artist)}

                {/* Role dropdown */}
                {canManage ? (
                  <select
                    value={artist.role}
                    onChange={(e) =>
                      updateRoleMutation.mutate({
                        profileId: artist.profile.id,
                        role: e.target.value,
                      })
                    }
                    className={`${controlCls} bg-panel-3 px-2.5 text-ink focus:outline-none`}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <span className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-ink-3 bg-panel-3">
                    {artist.role === "admin" && <Shield className="h-3 w-3" />}
                    {ARTIST_ROLE_LABELS[artist.role] || artist.role}
                  </span>
                )}

                {/* Actions */}
                {canManage && (
                  <div className="flex items-center gap-1.5">
                  {/* Invite button — show when artist has no linked user account */}
                  {!artist.profile.user_id && (
                    <>
                      {invitingId === artist.profile.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (inviteEmail.trim()) {
                              inviteMutation.mutate({
                                profileId: artist.profile.id,
                                email: inviteEmail.trim(),
                              });
                            }
                          }}
                          className="flex items-center gap-1.5"
                        >
                          <input
                            type="email"
                            required
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="email@example.com"
                            className={`${controlCls} w-44 bg-panel-3 px-2.5 text-ink placeholder:text-ink-4 focus:border-line-3 focus:outline-none`}
                          />
                          <button
                            type="submit"
                            disabled={inviteMutation.isPending}
                            className={`${controlCls} bg-panel-3 px-3 font-medium text-ink hover:bg-panel-2 disabled:opacity-50`}
                          >
                            Send
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setInvitingId(null);
                              setInviteEmail("");
                            }}
                            className={`${controlCls} px-2.5 text-ink-3 hover:bg-panel-2 hover:text-ink`}
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <button
                          onClick={() => setInvitingId(artist.profile.id)}
                          className={`${controlCls} flex items-center justify-center w-8 text-ink-3 transition-colors hover:bg-panel-2 hover:text-ink`}
                          title="Invite artist"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  )}

                  {/* Remove button */}
                  <button
                    onClick={() => {
                      if (confirm(`Remove ${artist.profile.display_name} from the organization?`)) {
                        removeMutation.mutate(artist.profile.id);
                      }
                    }}
                    className={`${controlCls} flex items-center justify-center w-8 text-ink-3 transition-colors hover:border-bad/50 hover:bg-bad/10 hover:text-bad`}
                    title="Remove artist"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Artist Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Artist</DialogTitle>
            <DialogDescription>
              Add an artist to your organization roster.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs text-ink-3">Name *</label>
              <input
                type="text"
                required
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="Artist or band name"
                className="input"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-ink-3">Email (optional)</label>
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="artist@example.com"
                className="input"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-ink-3">Role</label>
              <select
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                className="input"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {addEmail.trim() && (
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="checkbox"
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                  className="rounded border-line-3 bg-panel-3"
                />
                Send invite immediately
              </label>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowAddDialog(false)}
                className="btn btn--ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || !addName.trim()}
                className="btn btn--ghost px-4"
              >
                {createMutation.isPending ? "Adding..." : "Add Artist"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
